import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for existing user session in localStorage
    const savedUser = localStorage.getItem('advanced_making_user');
    if (savedUser) {
      try {
        setUser(JSON.parse(savedUser));
      } catch (error) {
        console.error('Error parsing saved user data:', error);
        localStorage.removeItem('advanced_making_user');
      }
    }
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Demo users for testing - in production, this would be handled by your backend
    const demoUsers = {
      'demo@advanced-making.com': {
        id: 'demo-user-1',
        email: 'demo@advanced-making.com',
        name: 'Demo User',
        avatar: null,
        plan: 'Premium',
        joinDate: new Date().toISOString(),
        preferences: {
          theme: 'light',
          notifications: true,
          publicProfile: false
        },
        achievements: [
          { id: 'first_enrollment', title: 'First Steps', description: 'Enrolled in your first course' },
          { id: 'early_adopter', title: 'Early Adopter', description: 'Joined Advanced Making' }
        ]
      },
      'student@example.com': {
        id: 'student-1',
        email: 'student@example.com',
        name: 'Alex Student',
        avatar: null,
        plan: 'Free',
        joinDate: new Date().toISOString(),
        preferences: {
          theme: 'light',
          notifications: true,
          publicProfile: true
        },
        achievements: []
      }
    };

    if (email in demoUsers && password === 'demo123') {
      const userData = demoUsers[email];
      setUser(userData);
      localStorage.setItem('advanced_making_user', JSON.stringify(userData));
      return { success: true, user: userData };
    } else {
      throw new Error('Invalid credentials');
    }
  };

  const register = async (userData) => {
    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    const newUser = {
      id: `user-${Date.now()}`,
      email: userData.email,
      name: userData.name,
      avatar: null,
      plan: 'Free',
      joinDate: new Date().toISOString(),
      preferences: {
        theme: 'light',
        notifications: true,
        publicProfile: false
      },
      achievements: [
        { id: 'welcome', title: 'Welcome!', description: 'Successfully created your Advanced Making account' }
      ]
    };

    setUser(newUser);
    localStorage.setItem('advanced_making_user', JSON.stringify(newUser));
    return { success: true, user: newUser };
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('advanced_making_user');
  };

  const updateProfile = async (updates) => {
    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const updatedUser = { ...user, ...updates };
    setUser(updatedUser);
    localStorage.setItem('advanced_making_user', JSON.stringify(updatedUser));
    return updatedUser;
  };

  const resetPassword = async (email) => {
    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // In a real app, this would trigger a password reset email
    console.log(`Password reset email sent to ${email}`);
    return { success: true };
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    updateProfile,
    resetPassword,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};